import React, { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { ComposableMap, ZoomableGroup, Geographies, Geography } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import Plus from 'theme/icons/plus';
import Minus from 'theme/icons/minus';
import _ from 'lodash';
import Label from './label';
// 20200302190814
// https://raw.githubusercontent.com/zcreativelabs/react-simple-maps/master/topojson-maps/world-10m.json
import geoUrl from './world-110m.json';

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  .rsm-geography {
    outline: 0;
  }

  & > svg {
    width: 100%;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  display: flex;
  flex-direction: column;

  & > button {
    width: 38px;
    height: 38px;
    background: ${themeGet('colors.white')};
    color: ${themeGet('colors.primary_300')};
    border: 1px solid ${themeGet('colors.primary_100')};

    & > span {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  & > button:first-child {
    border-bottom-width: 0px;
  }
`;

const StyledGeography = styled(Geography)`
  fill: #b0dae0;
  stroke: #f7f7f7;
  stroke-width: 0.75px;
`;

function Maps({ markers, projection = 'geoMercator', width = 800, height = 700, maxHeight }) {
  const [tooltipId, setTooltipId] = useState('');
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 0]);
  const [markersState, setMarkersState] = useState([]);

  const quantities = _.map(markers, 'quantity');
  const minQuantity = _.min(quantities);
  const maxQuantity = _.max(quantities);

  useEffect(() => {
    if (markers.length < 1) return setMarkersState([]);

    // Sort by quantity
    const markersSorted = markers.sort((a, b) => a.quantity - b.quantity);

    setMarkersState(markersSorted);

    setCenter(markersSorted[markersSorted.length - 1].coordinates);
  }, [markers]);

  const handleZoomIn = useCallback(() => {
    const newZoom = zoom + 1;

    if (newZoom > 9) {
      setZoom(9);
    } else {
      setZoom(newZoom);
    }
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    const newZoom = zoom - 1;

    if (newZoom < 1) {
      setZoom(1);
    } else {
      setZoom(newZoom);
    }
  }, [zoom]);

  const colorScale = scaleLinear().domain([minQuantity, maxQuantity]).range(['#b0dae0', '#0E4B53']);

  const handleMarkerClick = useCallback((v) => {
    setTooltipId(v);
  }, []);

  return (
    <Wrapper>
      <Controls>
        <button title="+" type="button" onClick={handleZoomIn}>
          <span>
            <Plus />
          </span>
        </button>
        <button title="-" type="button" onClick={handleZoomOut}>
          <span>
            <Minus />
          </span>
        </button>
      </Controls>
      <ComposableMap
        width={width}
        height={height}
        projection={projection}
        style={{
          maxHeight,
        }}
      >
        <ZoomableGroup
          /* filterZoomEvent={filterZoomEvent} */ zoom={zoom}
          maxZoom={10}
          center={center}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const d = markers.find((m) => m.country === geo.properties.NAME);
                return (
                  <Geography
                    id={geo.rsmKey}
                    key={geo.rsmKey}
                    geography={geo}
                    fill={d && d.quantity ? colorScale(d.quantity) : '#b0dae0'}
                    onMouseEnter={() => {
                      setTooltipId(`${geo.properties.NAME}`);
                    }}
                    onMouseLeave={() => {
                      setTooltipId("");
                    }}
                  />
                );
              })
            }
          </Geographies>
          {markersState.map(({ country, quantity, coordinates }) => (
            <Label
              key={country}
              width={width}
              height={height}
              projection={projection}
              isVisible={tooltipId === country}
              coordinates={coordinates}
              zoom={zoom}
              quantity={quantity}
              country={country}
            />
          ))}
        </ZoomableGroup>
      </ComposableMap>

    </Wrapper>
  );
}

export default Maps;
